import { useEffect, useState } from "react";
import { Logo } from "@/components/Logo";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Briefcase, Users, ClipboardCheck, Smartphone,
  CreditCard, UserCog, LogOut, Bell, ChevronDown, Wrench, CloudOff, RefreshCw,
  LayoutTemplate, CalendarClock, CalendarRange, FolderKanban, Settings, FileText, BookOpen, Truck, HardHat, DollarSign, Boxes, ScanBarcode, Camera, Inbox, FileCheck2, BellRing, Plus, Star,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { StatusPill } from "@/components/tac";
import { api } from "@/lib/api";
import ThemeToggle, { CodeGuard } from "@/components/ThemeToggle";
import { initSync, onOutboxChange, pendingCount, flush } from "@/lib/offline";

const NAV = [
  { to: "/new", label: "New Job", icon: Plus, roles: ["owner", "dispatcher", "technician"] },
  { to: "/dispatch", label: "Command", icon: LayoutDashboard, roles: ["owner", "dispatcher"] },
  { to: "/jobs", label: "Jobs", icon: Briefcase, roles: ["owner", "dispatcher", "technician", "subcontractor"] },
  { to: "/feed", label: "Photo Feed", icon: Camera, roles: ["owner", "dispatcher", "technician"] },
  { to: "/projects", label: "Projects", icon: FolderKanban, roles: ["owner", "dispatcher", "technician"] },
  { to: "/quotes", label: "Quotes", icon: FileText, roles: ["owner", "dispatcher"] },
  { to: "/sub", label: "My Work", icon: HardHat, roles: ["subcontractor"] },
  { to: "/pricebook", label: "Pricebook", icon: BookOpen, roles: ["owner", "dispatcher"] },
  { to: "/vendors", label: "Vendors", icon: Truck, roles: ["owner", "dispatcher"] },
  { to: "/payouts", label: "Payouts", icon: DollarSign, roles: ["owner", "dispatcher"] },
  { to: "/tech/today", label: "Field", icon: Smartphone, roles: ["owner", "dispatcher", "technician", "subcontractor"] },
  { to: "/review", label: "Review", icon: ClipboardCheck, roles: ["owner", "dispatcher", "technician"] },
  { to: "/customers", label: "Customers", icon: Users, roles: ["owner", "dispatcher"] },
  { to: "/reviews", label: "Reviews", icon: Star, roles: ["owner", "dispatcher"] },
  { to: "/fleet", label: "Fleet", icon: Wrench, roles: ["owner", "dispatcher", "technician"] },
  { to: "/inventory", label: "Inventory", icon: Boxes, roles: ["owner", "dispatcher", "technician"] },
  { to: "/assets", label: "Assets", icon: ScanBarcode, roles: ["owner", "dispatcher", "technician"] },
  { to: "/compliance", label: "Compliance", icon: CalendarClock, roles: ["owner", "dispatcher"] },
  { to: "/permits", label: "Permits", icon: FileCheck2, roles: ["owner", "dispatcher", "technician"] },
  { to: "/reminders", label: "Reminders", icon: BellRing, roles: ["owner", "dispatcher"] },
  { to: "/schedule", label: "Schedule", icon: CalendarRange, roles: ["owner", "dispatcher"] },
  { to: "/notifications", label: "Inbox", icon: Bell, roles: ["owner", "dispatcher", "technician"] },
  { to: "/leads", label: "Leads", icon: Inbox, roles: ["owner", "dispatcher"] },
  { to: "/team", label: "Team", icon: UserCog, roles: ["owner", "dispatcher"] },
  { to: "/templates", label: "Templates", icon: LayoutTemplate, roles: ["owner"] },
  { to: "/billing", label: "Billing", icon: CreditCard, roles: ["owner"] },
  { to: "/account", label: "Account", icon: Settings, roles: ["owner", "dispatcher", "technician", "subcontractor"] },
];

function SyncBadge() {
  const [n, setN] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    initSync();
    pendingCount().then(setN);
    const off = onOutboxChange(setN);
    const on = () => setOnline(true), offl = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", offl);
    return () => { off(); window.removeEventListener("online", on); window.removeEventListener("offline", offl); };
  }, []);
  if (online && n === 0) return null;
  return (
    <button data-testid="sync-badge" onClick={() => flush().catch(() => {})}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono-tac text-xs ${online ? "border-amber-500/40 bg-amber-950/40 text-amber-300" : "border-rose-500/40 bg-rose-950/40 text-rose-300"}`}>
      {online ? <RefreshCw className="h-3.5 w-3.5" /> : <CloudOff className="h-3.5 w-3.5" />}
      {n > 0 ? `${n} queued` : "offline"}
    </button>
  );
}

function NotifBell() {
  const [count, setCount] = useState(0);
  const nav = useNavigate();
  const load = () => api.get("/notifications/unread").then((r) => setCount(r.data.count)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  const open = async () => { try { await api.post("/notifications/mark-read"); } catch { /* ignore */ } setCount(0); nav("/notifications"); };
  return (
    <button data-testid="topbar-alerts" onClick={open} className="relative rounded-lg p-2 text-slate-400 hover:text-white">
      <Bell className="h-5 w-5" />
      {count > 0 && <span data-testid="notif-badge" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 font-mono-tac text-[9px] font-bold text-white">{count}</span>}
    </button>
  );
}

export default function AppLayout() {
  const { user, workspace, logout } = useAuth();
  const nav = useNavigate();
  const items = NAV.filter((n) => n.roles.includes(user?.role));
  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-200">
      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col justify-between border-r border-slate-800 bg-[#0B0F17] p-4 md:flex">
        <div>
          <Link to="/dispatch" className="mb-8 flex flex-col items-start gap-2 px-2">
            <Logo />
            <div className="font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">{workspace?.name}</div>
          </Link>
          <nav className="space-y-1">
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? "bg-amber-500/10 text-amber-400" : "text-slate-400 hover:bg-slate-800/60 hover:text-white"}`}>
                <n.icon className="h-4.5 w-4.5" /> {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="rounded-xl border border-slate-800 p-3">
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-full bg-cyan-500/15 font-display text-sm font-bold text-cyan-300">
              {user?.name?.[0] || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-white">{user?.name}</div>
              <div className="font-mono-tac text-[10px] uppercase tracking-wider text-slate-500">{user?.role}</div>
            </div>
            <button data-testid="logout-btn" onClick={doLogout} className="text-slate-500 hover:text-rose-400"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </aside>

      {/* Topbar */}
      <div className="md:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-800 bg-[#0F172A]/90 px-5 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <span className="font-mono-tac text-[11px] uppercase tracking-widest text-slate-500">Workspace</span>
            <span className="text-sm font-medium text-white">{workspace?.name}</span>
            <StatusPill status={workspace?.subscription_status} />
          </div>
          <div className="flex items-center gap-3">
            <SyncBadge />
            <CodeGuard />
            <ThemeToggle />
            <NotifBell />
            <button onClick={doLogout} className="hidden items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-slate-400 hover:text-white md:flex">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </header>

        <main className="pb-24 md:pb-8"><Outlet /></main>
      </div>

      {/* Mobile bottom nav (hidden while the full-screen wizard owns the viewport) */}
      {useLocation().pathname !== "/new" && (
      <nav data-testid="mobile-bottom-nav" className="fixed bottom-0 left-0 right-0 z-50 flex h-16 items-center justify-around border-t border-slate-800 bg-[#0B0F17]/95 px-2 backdrop-blur-lg md:hidden">
        {items.slice(0, 5).map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`mobilenav-${n.label.toLowerCase()}`}
            className={({ isActive }) => `flex min-w-[48px] flex-col items-center gap-1 rounded-lg px-2 py-1.5 text-[10px] ${isActive ? "text-amber-400" : "text-slate-500"}`}>
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
      )}
    </div>
  );
}
